/**
 * prompts.js - turning a support rung into something Pip can say and the
 * street can show.
 *
 * PURE. The words come from content/voice.js; which words, from the engine's
 * rung. Line SELECTION is injected (`choose`) so the anti-repeat bag stays in
 * one place and this file stays testable.
 *
 * It lived inside DoorKnock.jsx until the trial loop moved out of the
 * component: the loop asks a game for a prompt, and a prompt that can only be
 * built inside a React render cannot be built by a test.
 */

import { rungIndexOf, rungIdAt } from '../../shared/support.js';
import * as VOICE from '../content/voice.js';
import { getCreature, getCarrier, getPlace, canonicalPhrase } from '../content/creatures.js';

/**
 * Templates for a rung, falling back toward MORE support when the phase does
 * not define the exact rung. More help than the rung intended costs a box; a
 * mute Pip costs the trial.
 */
export function templatesFor(phase, rungId) {
  const byRung = VOICE.PROMPTS[phase] || {};
  if (byRung[rungId] && byRung[rungId].length) return byRung[rungId];
  for (let index = rungIndexOf(rungId) - 1; index >= 0; index -= 1) {
    const id = rungIdAt(index);
    if (byRung[id] && byRung[id].length) return byRung[id];
  }
  const any = Object.values(byRung).find((lines) => lines && lines.length);
  return any || ['Your turn.'];
}

/** What is drawn on the door at this rung, if anything. */
export function cueFor({ rungId, cueStyle, creatureId }) {
  const creature = getCreature(creatureId);
  if (!creature) return null;
  if (rungId === 'full_model') return { kind: 'word', text: creature.word };
  if (rungId === 'partial_verbal') {
    const head = creature.firstLetter || creature.word[0].toUpperCase();
    if (cueStyle === 'partial_word') {
      return {
        kind: 'partial_word',
        text: `${head}${'_'.repeat(Math.max(2, creature.word.length - head.length))}`,
      };
    }
    if (cueStyle === 'first_letter') return { kind: 'first_letter', text: head };
  }
  return null;
}

const carrierWords = (content) =>
  (content.carriers || []).map((id) => getCarrier(id).word).join(' or ');

/**
 * @returns {{text:string, cue:object|null, chips:Array|null, showsAnswer:boolean}}
 */
export function buildPrompt({ phase, rungId, target, content, choose }) {
  const creature = getCreature(target.creatureId);
  const carrier = target.carrierId ? getCarrier(target.carrierId) : null;

  const text = VOICE.renderLine(choose(templatesFor(phase, rungId)), {
    word: creature.word,
    phrase: canonicalPhrase(target),
    letter: creature.firstLetter || creature.word[0].toUpperCase(),
    carrier: carrier ? carrier.word : '',
    carriers: carrierWords(content),
    clue: creature.clue,
    place: target.placeId ? getPlace(target.placeId).label : '',
  });

  return {
    text,
    cue: cueFor({ rungId, cueStyle: content.cueStyle, creatureId: target.creatureId }),
    // Does this prompt hand the child the answer? Drives the record's
    // modelPlayed, which gates Phase 2 B and C.
    showsAnswer: rungId === 'full_model',
    chips: phase === 3 && rungId !== 'gestural'
      ? (content.carriers || []).map((id) => getCarrier(id))
      : null,
  };
}

/** Pip's semantic clue. It must never contain its own answer. */
export function buildHint({ phase, target, content, choose }) {
  const creature = getCreature(target.creatureId);
  return VOICE.renderLine(choose(VOICE.HINTS[phase] || VOICE.HINTS[2]), {
    clue: creature.clue,
    carriers: carrierWords(content),
    place: target.placeId ? getPlace(target.placeId).label : '',
  });
}

export default { templatesFor, cueFor, buildPrompt, buildHint };
